import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth, hasRole } from '@/lib/auth';
import { I18nProvider } from '@/lib/i18n';
import BrandProvider from '@/lib/brand';
import AppShell from '@/components/AppShell';
import Login from '@/pages/Login';
import QuickReceipt from '@/pages/QuickReceipt';
import { SessionTimeoutGuard } from '@/components/SessionTimeoutGuard';
import { CookieConsentBanner } from '@/components/CookieConsentBanner';
import { LegalAcceptanceGate } from '@/components/LegalAcceptanceGate';
import { NotFoundPage } from '@/pages/ErrorPages';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Route-level code-splitting: less-used pages load on demand.
const Clients = lazy(() => import('@/pages/Clients'));
const ClientDetail = lazy(() => import('@/pages/ClientDetail'));
const Receipts = lazy(() => import('@/pages/Receipts'));
const ReceiptDetail = lazy(() => import('@/pages/ReceiptDetail'));
const ReceiptEditorPage = lazy(() => import('@/pages/ReceiptEditorPage'));
const PaymentReceipts = lazy(() => import('@/pages/PaymentReceipts'));
const Coupons = lazy(() => import('@/pages/Coupons'));
const PaymentReturn = lazy(() => import('@/pages/PaymentReturn'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Reports = lazy(() => import('@/pages/Reports'));
const Services = lazy(() => import('@/pages/Services'));
const Users = lazy(() => import('@/pages/Users'));
const Settings = lazy(() => import('@/pages/Settings'));
const AuditLog = lazy(() => import('@/pages/AuditLog'));
const DeletionRequests = lazy(() => import('@/pages/DeletionRequests'));
const Expenses = lazy(() => import('@/pages/Expenses'));
const Accounting = lazy(() => import('@/pages/Accounting'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Profile = lazy(() => import('@/pages/Profile'));
const LegalDocument = lazy(() => import('@/pages/LegalDocument'));
// Tanda 17: Super Admin Console + public signup + firm setup + tutorial
const SuperAdminFirms = lazy(() => import('@/pages/SuperAdminFirms'));
const SuperAdminTour = lazy(() => import('@/pages/SuperAdminTour'));
const SuperAdminTenantsDiagnostics = lazy(() => import('@/pages/SuperAdminTenantsDiagnostics'));
const SuperAdminSystemStatus = lazy(() => import('@/pages/SuperAdminSystemStatus'));
const FirmTourEditor = lazy(() => import('@/pages/FirmTourEditor'));
const FirmAssistantPage = lazy(() => import('@/pages/FirmAssistantPage'));
const DocumentAnalyzerPage = lazy(() => import('@/pages/DocumentAnalyzerPage'));
const ExecutiveReportsPage = lazy(() => import('@/pages/ExecutiveReportsPage'));
const ClientPulsePage = lazy(() => import('@/pages/ClientPulsePage'));
const PaymentPredictionsPage = lazy(() => import('@/pages/PaymentPredictionsPage'));
const MeruitPage = lazy(() => import('@/pages/MeruitPage'));
const MonthlyClosePage = lazy(() => import('@/pages/MonthlyClosePage'));
const AuditTimelinePage = lazy(() => import('@/pages/AuditTimelinePage'));
const BillingAdjustmentsReport = lazy(() => import('@/pages/BillingAdjustmentsReport'));
const PublicPayPage = lazy(() => import('@/pages/PublicPayPage'));
const PendingPaymentsPage = lazy(() => import('@/pages/PendingPaymentsPage'));
const PublicSignup = lazy(() => import('@/pages/PublicSignup'));
const FirmSetup = lazy(() => import('@/pages/FirmSetup'));
const SystemOps = lazy(() => import('@/pages/SystemOps'));
const Trust = lazy(() => import('@/pages/Trust'));
const Recurring = lazy(() => import('@/pages/Recurring'));
const PortalLogin = lazy(() => import('@/pages/portal/PortalLogin'));
const PortalVerify = lazy(() => import('@/pages/portal/PortalVerify'));
const PortalSetup = lazy(() => import('@/pages/portal/PortalSetup'));
const PortalLayout = lazy(() => import('@/pages/portal/PortalLayout'));
const PortalDashboard = lazy(() => import('@/pages/portal/PortalDashboard'));
const PortalReceipts = lazy(() => import('@/pages/portal/PortalReceipts'));
const PortalReceiptDetail = lazy(() => import('@/pages/portal/PortalReceiptDetail'));
const PortalInvoices = lazy(() => import('@/pages/portal/PortalInvoices'));
const PortalPaymentReceipts = lazy(() => import('@/pages/portal/PortalPaymentReceipts'));
const PortalTrust = lazy(() => import('@/pages/portal/PortalTrust'));

function PageLoader() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center" data-testid="page-loader">
      <div className="h-8 w-8 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
    </div>
  );
}

function RequireAuth({ roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !hasRole(user, ...roles)) return <Navigate to="/sin-acceso" replace />;
  return <Outlet />;
}

function AppRoutes() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<PublicSignup />} />
        <Route path="/firm-setup" element={<FirmSetup />} />
        <Route path="/olvide-password" element={<ForgotPassword />} />
        <Route path="/restablecer" element={<ResetPassword />} />
        <Route path="/legal/:slug" element={<LegalDocument />} />
        <Route path="/pago/exito" element={<PaymentReturn kind="success" />} />
        <Route path="/pago/cancelado" element={<PaymentReturn kind="cancel" />} />
        <Route path="/pagar/:token" element={<PublicPayPage />} />

        {/* Portal (client-facing) routes — completely separate from admin app */}
        <Route path="/portal/login" element={<PortalLogin />} />
        <Route path="/portal/verify" element={<PortalVerify />} />
        <Route path="/portal/setup" element={<PortalSetup />} />
        <Route path="/portal" element={<PortalLayout />}>
          <Route index element={<Navigate to="/portal/dashboard" replace />} />
          <Route path="dashboard" element={<PortalDashboard />} />
          <Route path="recibos" element={<PortalReceipts />} />
          <Route path="recibos/:id" element={<PortalReceiptDetail />} />
          <Route path="facturas" element={<PortalInvoices />} />
          <Route path="facturas/:id" element={<PortalReceiptDetail />} />
          <Route path="recibos-pagados" element={<PortalPaymentReceipts />} />
          <Route path="fideicomiso" element={<PortalTrust />} />
        </Route>

        <Route element={<RequireAuth />}>
          <Route element={<AppShell />}>
            <Route path="/" element={<QuickReceipt />} />
            <Route path="/nuevo-recibo" element={<QuickReceipt />} />
            <Route path="/perfil" element={<Profile />} />
            <Route path="/clientes" element={<Clients />} />
            <Route path="/clientes/:id" element={<ClientDetail />} />
            <Route path="/recibos" element={<Receipts />} />
            <Route path="/recibos/:id" element={<ReceiptDetail />} />
            <Route path="/recibos/:id/editar" element={<ReceiptEditorPage />} />
            <Route path="/recibos/:id/historial" element={<AuditTimelinePage />} />
            <Route path="/pagos-recibos" element={<PaymentReceipts />} />
            <Route path="/cupones" element={<Coupons />} />
            <Route path="/servicios" element={<Services />} />
            <Route path="/gastos" element={<Expenses />} />
            <Route path="/contabilidad" element={<Accounting />} />
            <Route path="/fideicomiso" element={<Trust />} />
            <Route path="/recurrentes" element={<Recurring />} />
            <Route path="/solicitudes" element={<DeletionRequests />} />
            <Route path="/asistente" element={<FirmAssistantPage />} />
            <Route path="/analizar-documento" element={<DocumentAnalyzerPage />} />
            <Route path="/pulso-clientes" element={<ClientPulsePage />} />
            <Route path="/prediccion-pagos" element={<PaymentPredictionsPage />} />
            <Route path="/meruit" element={<MeruitPage />} />
            <Route path="/meruit/cierre-mensual" element={<MonthlyClosePage />} />
            <Route path="/sin-acceso" element={<NotFoundPage />} />

            <Route element={<RequireAuth roles={['admin', 'accountant']} />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/reportes" element={<Reports />} />
              <Route path="/reportes/ajustes-meruit" element={<BillingAdjustmentsReport />} />
              <Route path="/pagos-pendientes" element={<PendingPaymentsPage />} />
              <Route path="/reportes-ia" element={<ExecutiveReportsPage />} />
            </Route>

            <Route element={<RequireAuth roles={['admin']} />}>
              <Route path="/usuarios" element={<Users />} />
              <Route path="/configuracion" element={<Settings />} />
              <Route path="/tour-personalizado" element={<FirmTourEditor />} />
              <Route path="/auditoria" element={<AuditLog />} />
              <Route path="/super-admin/firmas" element={<SuperAdminFirms />} />
              <Route path="/super-admin/tour" element={<SuperAdminTour />} />
              <Route path="/super-admin/firmas-diagnostico" element={<SuperAdminTenantsDiagnostics />} />
              <Route path="/super-admin/estado-sistema" element={<SuperAdminSystemStatus />} />
              <Route path="/estado-sistema" element={<SystemOps />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <I18nProvider>
          <BrandProvider>
            <BrowserRouter>
              <AppRoutes />
              <SessionTimeoutGuard />
              <LegalAcceptanceGate />
              <CookieConsentBanner />
              <Toaster position="top-right" richColors closeButton />
            </BrowserRouter>
          </BrandProvider>
        </I18nProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
